import { useRef, useEffect } from 'react';
import { Renderer, Program, Geometry, Mesh } from 'ogl';

// ============================================================================
// CONSTANTS
// ============================================================================
const DEFAULT_COLOR = '#3b82f6';  // Matches depth chart color
const DEFAULT_OPACITY = 0.5;
const DEFAULT_BUBBLE_COUNT = 50;
const DEFAULT_SPEED = 1.0;
const DEFAULT_MIN_SIZE = 4;
const DEFAULT_MAX_SIZE = 24;

// ============================================================================
// HELPERS
// ============================================================================
const hexToRgb = (hex: string): [number, number, number] => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m ? [parseInt(m[1], 16) / 255, parseInt(m[2], 16) / 255, parseInt(m[3], 16) / 255] : [1, 1, 1];
};

// Deterministic random based on seed
const seededRandom = (seed: number): number => {
  const x = Math.sin(seed * 127.1) * 43758.5453123;
  return x - Math.floor(x);
};

// ============================================================================
// COMPONENT INTERFACE
// ============================================================================
interface BubblesProps {
  bubbleCount?: number;
  speed?: number;
  minSize?: number;
  maxSize?: number;
  color?: string;
  opacity?: number;
  className?: string;
}

// ============================================================================
// COMPONENT
// ============================================================================
const Bubbles = ({
  bubbleCount = DEFAULT_BUBBLE_COUNT,
  speed = DEFAULT_SPEED,
  minSize = DEFAULT_MIN_SIZE,
  maxSize = DEFAULT_MAX_SIZE,
  color = DEFAULT_COLOR,
  opacity = DEFAULT_OPACITY,
  className = ''
}: BubblesProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const rendererRef = useRef<Renderer | null>(null);
  const programRef = useRef<Program | null>(null);
  const meshRef = useRef<Mesh | null>(null);
  const animationIdRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;

    const container = containerRef.current;

    // Initialize renderer
    const renderer = new Renderer({
      dpr: Math.min(window.devicePixelRatio, 2),
      alpha: true,
      premultipliedAlpha: true,
    });
    rendererRef.current = renderer;

    const gl = renderer.gl;
    gl.canvas.style.width = '100%';
    gl.canvas.style.height = '100%';
    gl.clearColor(0, 0, 0, 0);
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

    container.appendChild(gl.canvas);

    // Generate bubble instance data
    const offsets = new Float32Array(bubbleCount * 2);    // x, y position offset
    const sizes = new Float32Array(bubbleCount);          // radius
    const phases = new Float32Array(bubbleCount);         // animation phase offset
    const speeds = new Float32Array(bubbleCount);         // individual speed multiplier

    for (let i = 0; i < bubbleCount; i++) {
      offsets[i * 2] = seededRandom(i * 1.1);             // x: 0-1 normalized
      offsets[i * 2 + 1] = seededRandom(i * 2.2);         // y: 0-1 normalized
      sizes[i] = minSize + seededRandom(i * 3.3) * (maxSize - minSize);
      phases[i] = seededRandom(i * 4.4) * Math.PI * 2;
      speeds[i] = 0.7 + seededRandom(i * 5.5) * 0.6;      // 0.7-1.3x speed variation
    }

    // Quad vertices (two triangles)
    const position = new Float32Array([
      -1, -1,
       1, -1,
       1,  1,
      -1, -1,
       1,  1,
      -1,  1,
    ]);

    const uv = new Float32Array([
      0, 0,
      1, 0,
      1, 1,
      0, 0,
      1, 1,
      0, 1,
    ]);

    const geometry = new Geometry(gl, {
      position: { size: 2, data: position },
      uv: { size: 2, data: uv },
      offset: { instanced: 1, size: 2, data: offsets },
      size: { instanced: 1, size: 1, data: sizes },
      phase: { instanced: 1, size: 1, data: phases },
      speedMult: { instanced: 1, size: 1, data: speeds },
    });

    const vertex = `
      attribute vec2 position;
      attribute vec2 uv;
      attribute vec2 offset;
      attribute float size;
      attribute float phase;
      attribute float speedMult;

      uniform vec2 uResolution;
      uniform vec2 uWorldSize;  // Fixed size for physics (never changes)
      uniform float uTime;
      uniform float uSpeed;

      varying vec2 vUv;
      varying float vAlpha;

      void main() {
        vUv = uv;

        // Calculate bubble center in WORLD coordinates (stable)
        float time = uTime * uSpeed * speedMult;

        // Horizontal position with gentle wobble (in world space)
        float wobble = sin(time * 2.0 + phase) * size * 0.5;
        float worldX = offset.x * uWorldSize.x + wobble;

        // Vertical position - rise and loop (in world space)
        float cycleHeight = uWorldSize.y + size * 2.0;
        float baseY = offset.y * cycleHeight;
        float worldY = mod(baseY + time * 30.0, cycleHeight) - size;

        // Scale factor (for bubble sizes)
        float scaleX = uResolution.x / uWorldSize.x;
        float screenSize = size * scaleX;

        // Map X directly (width usually doesn't change)
        float screenX = worldX * scaleX;

        // Map Y anchored to TOP of screen (iOS address bar changes bottom, not top)
        // worldY=0 is bottom of world, we want it to map to bottom of screen
        // But we anchor to top, so offset by the height difference
        float heightDiff = uResolution.y - uWorldSize.y;
        float screenY = worldY + heightDiff;

        // Fade in at bottom, fade out at top (in screen space)
        float fadeIn = smoothstep(0.0, screenSize * 2.0, screenY);
        float fadeOut = smoothstep(uResolution.y, uResolution.y - screenSize * 2.0, screenY);
        vAlpha = fadeIn * fadeOut;

        // Position quad vertices (in screen space)
        vec2 center = vec2(screenX, screenY);
        vec2 pos = center + position * screenSize;

        // Convert to clip space
        vec2 clip = (pos / uResolution) * 2.0 - 1.0;
        gl_Position = vec4(clip, 0.0, 1.0);
      }
    `;

    const fragment = `
      precision highp float;

      uniform vec3 uColor;
      uniform float uOpacity;

      varying vec2 vUv;
      varying float vAlpha;

      void main() {
        // Distance from center (0 at center, 1 at edge)
        vec2 uv = vUv * 2.0 - 1.0;
        float dist = length(uv);

        // Soft circular falloff (no discard - use alpha instead for iOS compatibility)
        float circle = 1.0 - smoothstep(0.8, 1.0, dist);

        // Bubble body - subtle, fades toward edge
        float body = 0.15 * (1.0 - dist * 0.5);

        // Specular highlight (top-left) - bright white spot
        vec2 highlightPos = uv + vec2(0.4, -0.4);
        float highlight = exp(-length(highlightPos) * 4.0) * 0.8;

        // Glowing edge/border highlight (thin band near edge)
        float edgeGlow = smoothstep(0.6, 0.8, dist) * smoothstep(1.0, 0.85, dist) * 0.5;

        // Combine - circle masks everything
        float alpha = circle * (body + edgeGlow);
        vec3 col = uColor + vec3(highlight + edgeGlow * 0.5);

        // Output premultiplied alpha for consistent cross-browser rendering
        float finalAlpha = alpha * vAlpha * uOpacity;
        gl_FragColor = vec4(col * finalAlpha, finalAlpha);
      }
    `;

    // Capture initial size as fixed world size
    const { clientWidth: initWidth, clientHeight: initHeight } = container;

    const program = new Program(gl, {
      vertex,
      fragment,
      uniforms: {
        uResolution: { value: [initWidth, initHeight] },
        uWorldSize: { value: [initWidth, initHeight] },  // Set once, never changes
        uTime: { value: 0 },
        uSpeed: { value: speed },
        uColor: { value: hexToRgb(color) },
        uOpacity: { value: opacity },
      },
      transparent: true,
      depthTest: false,
    });
    programRef.current = program;

    const mesh = new Mesh(gl, { geometry, program });
    meshRef.current = mesh;

    const updateSize = () => {
      if (!container || !renderer) return;
      const { clientWidth, clientHeight } = container;
      renderer.setSize(clientWidth, clientHeight);
      // Only update resolution, NOT world size
      program.uniforms.uResolution.value = [clientWidth, clientHeight];
    };

    startTimeRef.current = performance.now();

    const loop = () => {
      if (!rendererRef.current || !meshRef.current || !programRef.current) return;

      const elapsed = (performance.now() - startTimeRef.current) * 0.001;
      programRef.current.uniforms.uTime.value = elapsed;

      // Clear before rendering
      gl.clear(gl.COLOR_BUFFER_BIT);
      rendererRef.current.render({ scene: meshRef.current });
      animationIdRef.current = requestAnimationFrame(loop);
    };

    window.addEventListener('resize', updateSize);
    updateSize();
    animationIdRef.current = requestAnimationFrame(loop);

    return () => {
      if (animationIdRef.current) {
        cancelAnimationFrame(animationIdRef.current);
      }
      window.removeEventListener('resize', updateSize);

      if (renderer) {
        const canvas = renderer.gl.canvas;
        if (canvas.parentNode) {
          canvas.parentNode.removeChild(canvas);
        }
      }

      rendererRef.current = null;
      programRef.current = null;
      meshRef.current = null;
    };
  }, [bubbleCount, speed, minSize, maxSize, color, opacity]);

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 pointer-events-none z-[20] overflow-hidden ${className}`.trim()}
    />
  );
};

export default Bubbles;
